<script>
import * as THREE from 'three'

export default {
  name: 'PanoMesh',
  inject: ['getScene', 'sceneReady', 'getViewer', 'addObject'],
  props: {
    points: {
      type: Array,
      default: () => ([])
    },
    color: {
      type: String,
      default: '#0000ff99'
    },
    wireframe: {
      type: Boolean,
      default: false
    },
    autoClose: {
      type: Boolean,
      default: true
    },
    hoverCursor: {
      type: String,
      default: 'pointer'
    },
    hoverColor: {
      type: String,
      default: '#0000ffff'
    }
  },
  data: () => ({
    vertices: [],
    geometry: null,
    material: null,
    mesh: null
  }),
  render () {
    return null
  },
  watch: {
    canMount () {
      if (this.sceneReady) {
        this.createShape()
      }
    },
    wireframe () {
      this.mesh.material.wireframe = this.wireframe
    },
    points () {
      if (this.mesh === null) {
        this.createShape()
      } else {
        this.updateShape()
      }
    },
    color () {
      this.setColor(this.color)
    },
    hoverColor () {
      this.mesh.hoverColor = this.hoverColor
    }
  },
  computed: {
    canMount () {
      return this.sceneReady()
    }
  },
  mounted () {
    if (this.canMount) {
      this.createShape()
    }
  },
  methods: {
    extractColor (hex) {
      return hex.slice(0, 7)
    },
    extractOpacity (hex) {
      return parseInt(hex.slice(7, 9), 16) / 255
    },
    createShape () {
      // Generate Vector3D
      if (this.points.length < 3) {
        return
      }

      this.points.forEach(point => {
        this.vertices.push(new THREE.Vector3(point.x, point.y, point.z))
      })

      // Auto close
      if (this.autoClose && this.points[0] !== this.points[this.points.length - 1]) {
        this.vertices.push(new THREE.Vector3(this.points[0].x, this.points[0].y, this.points[0].z))
      }

      const normal = new THREE.Vector3(0, 1, 0)
      const normalZ = new THREE.Vector3(0, 0, 1)
      const quaternion = new THREE.Quaternion().setFromUnitVectors(normal, normalZ)
      const quaternionBack = new THREE.Quaternion().setFromUnitVectors(normalZ, normal)

      this.vertices.forEach(point => {
        point.applyQuaternion(quaternion)
      })

      const shape = new THREE.Shape(this.vertices)
      const shapeGeom = new THREE.ShapeGeometry(shape)

      this.vertices.forEach(point => {
        point.applyQuaternion(quaternionBack)
      })

      shapeGeom.vertices = this.vertices

      if (this.mesh === null) {
        this.mesh = new THREE.Mesh(shapeGeom, new THREE.MeshBasicMaterial({
          color: this.extractColor(this.color),
          wireframe: this.wireframe,
          side: THREE.DoubleSide,
          transparent: true,
          opacity: this.extractOpacity(this.color)
        }))

        this.mesh.onLeave = this.onLeave
        this.mesh.onHover = this.onHover
        this.mesh.onClick = this.onClick
        this.mesh.hoverCursor = this.hoverCursor
        this.mesh.color = this.color
        this.mesh.hoverColor = this.hoverColor

        this.addObject(this.mesh)
        this.getScene().add(this.mesh)
      } else {
        this.mesh.geometry = shapeGeom
      }
    },
    updateShape () {
      this.vertices = []
      this.createShape()
    },
    setColor (hex) {
      this.mesh.material.color = new THREE.Color(this.extractColor(hex))
      this.mesh.material.opacity = this.extractOpacity(hex)
    },
    setOpacity (opacity) {
      this.mesh.material.opacity = opacity
    },
    onLeave () {
      this.setColor(this.color)
      this.$emit('leave', this)
    },
    onHover () {
      this.setColor(this.hoverColor)
      this.$emit('hover', this)
    },
    onClick () {
      this.$emit('click', this)
    }
  },
  destroyed () {
    if (this.mesh !== null) {
      this.getScene().remove(this.mesh)
      this.mesh.geometry.dispose()
      this.mesh.material.dispose()
      this.mesh.visible = false
    }
  }
}
</script>
